import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  Tabs, Tab, Alert, CircularProgress, LinearProgress, List, ListItem,
  ListItemText, ListItemIcon, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Switch, FormControlLabel
} from '@mui/material';
import {
  Storage as StorageIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  PlayArrow as ExecuteIcon,
  ExpandMore as ExpandMoreIcon,
  CloudDownload as DownloadIcon,
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  TableChart as TableIcon,
  Assessment as AssessmentIcon,
  Build as BuildIcon,
  History as HistoryIcon,
  ViewColumn as ColumnIcon,
  Key as KeyIcon,
  Link as LinkIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import { GradientButton, GlassButton } from './common/ModernButton';
import AlertSnackbar from './AlertSnackbar';
import axiosInstance from './axiosConfig';

const DatabaseManager = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [schemaComparison, setSchemaComparison] = useState(null);
  const [migrations, setMigrations] = useState([]);
  const [migrationHistory, setMigrationHistory] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedMigration, setSelectedMigration] = useState(null);
  const [showSqlDialog, setShowSqlDialog] = useState(false);
  const [sqlCode, setSqlCode] = useState('');
  const [executingSql, setExecutingSql] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchDatabaseStatus();
    fetchMigrations();
    fetchMigrationHistory();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchDatabaseStatus();
      }, 10000); // Refresh cada 10 segundos
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchDatabaseStatus = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/database/status');
      setDbStatus(response.data);
      showSnackbar('Estado de la base de datos actualizado', 'success');
    } catch (error) {
      console.error('Error fetching database status:', error);
      showSnackbar('Error al obtener el estado de la base de datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchemaComparison = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/database/compare-schema');
      setSchemaComparison(response.data);
      showSnackbar('Comparación de esquema completada', 'success');
    } catch (error) {
      console.error('Error comparing schema:', error);
      showSnackbar('Error al comparar esquemas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMigrations = async () => {
    try {
      const response = await axiosInstance.get('/api/database/migrations/available');
      setMigrations(response.data);
    } catch (error) {
      console.error('Error fetching migrations:', error);
    }
  };

  const fetchMigrationHistory = async () => {
    try {
      const response = await axiosInstance.get('/api/database/migrations/history');
      setMigrationHistory(response.data);
    } catch (error) {
      console.error('Error fetching migration history:', error);
    }
  };

  const executeMigration = async (migrationId) => {
    setExecutingSql(true);
    try {
      const response = await axiosInstance.post(`/api/database/migrations/execute/${migrationId}`);
      showSnackbar('Migración ejecutada con éxito', 'success');
      fetchDatabaseStatus();
      fetchMigrationHistory();
      fetchMigrations();
    } catch (error) {
      console.error('Error executing migration:', error);
      showSnackbar('Error al ejecutar la migración: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setExecutingSql(false);
    }
  };

  const executeCustomSql = async () => {
    if (!sqlCode.trim()) {
      showSnackbar('Por favor ingresa código SQL', 'warning');
      return;
    }

    setExecutingSql(true);
    try {
      const response = await axiosInstance.post('/api/database/execute-sql', { sql: sqlCode });
      showSnackbar('SQL ejecutado con éxito', 'success');
      setSqlCode('');
      setShowSqlDialog(false);
      fetchDatabaseStatus();
    } catch (error) {
      console.error('Error executing SQL:', error);
      showSnackbar('Error al ejecutar SQL: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setExecutingSql(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const response = await axiosInstance.get('/api/database/backup', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${new Date().toISOString()}.sql`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar('Backup descargado con éxito', 'success');
    } catch (error) {
      console.error('Error downloading backup:', error);
      showSnackbar('Error al descargar backup', 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copiado al portapapeles', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const renderDashboardTab = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Estado de la BD"
            value={dbStatus?.connected ? 'Conectada' : 'Desconectada'}
            subtitle={dbStatus?.database || 'N/A'}
            icon={<StorageIcon />}
            color={dbStatus?.connected ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Tablas"
            value={dbStatus?.tables?.length?.toString() || '0'}
            subtitle={`${dbStatus?.missingTables?.length || 0} faltantes`}
            icon={<TableIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Migraciones"
            value={migrations.filter(m => !m.executed).length.toString()}
            subtitle="Pendientes"
            icon={<BuildIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Salud del Sistema"
            value={dbStatus?.health || 'Unknown'}
            subtitle="Estado general"
            icon={<AssessmentIcon />}
            color={getHealthColor(dbStatus?.health)}
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <ModernCard title="Acciones Rápidas" sx={{ mb: 3 }}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <GradientButton
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={fetchDatabaseStatus}
              disabled={loading}
            >
              Actualizar Estado
            </GradientButton>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <GradientButton
              fullWidth
              startIcon={<AssessmentIcon />}
              onClick={fetchSchemaComparison}
              disabled={loading}
            >
              Comparar Esquema
            </GradientButton>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <GradientButton
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={downloadBackup}
            >
              Descargar Backup
            </GradientButton>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <GradientButton
              fullWidth
              startIcon={<CodeIcon />}
              onClick={() => setShowSqlDialog(true)}
            >
              Ejecutar SQL
            </GradientButton>
          </Grid>
        </Grid>
        <Box sx={{ p: 2, pt: 0 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="primary"
              />
            }
            label="Auto-actualizar cada 10s"
          />
        </Box>
      </ModernCard>

      {/* Database Info */}
      {dbStatus && (
        <ModernCard title="Información de la Base de Datos" sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Versión de PostgreSQL:</strong> {dbStatus.version || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Base de datos:</strong> {dbStatus.database || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Usuario:</strong> {dbStatus.user || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Tamaño de BD:</strong> {dbStatus.size || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Conexiones activas:</strong> {dbStatus.connections || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Última actualización:</strong> {new Date().toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </ModernCard>
      )}

      {/* Tables Overview */}
      {dbStatus?.tables && (
        <ModernCard title="Resumen de Tablas">
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Tablas existentes en tu base de datos:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {dbStatus.tables.map((table) => (
                <Chip
                  key={table}
                  label={table}
                  icon={<TableIcon />}
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
            {dbStatus.missingTables?.length > 0 && (
              <>
                <Typography variant="body2" color="error" gutterBottom sx={{ mt: 3 }}>
                  Tablas faltantes (requeridas):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {dbStatus.missingTables.map((table) => (
                    <Chip
                      key={table}
                      label={table}
                      icon={<ErrorIcon />}
                      color="error"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </ModernCard>
      )}
    </Box>
  );

  const renderSchemaTab = () => (
    <Box>
      <ModernCard title="Comparación de Esquema" sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta herramienta compara tu base de datos actual con el esquema objetivo y te muestra qué falta.
          </Alert>
          {!schemaComparison ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <GradientButton
                startIcon={<AssessmentIcon />}
                onClick={fetchSchemaComparison}
                disabled={loading}
              >
                Iniciar Comparación
              </GradientButton>
            </Box>
          ) : (
            <Box>
              {schemaComparison.tables?.map((table) => (
                <Accordion key={table.name} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <TableIcon />
                      <Typography variant="h6">{table.name}</Typography>
                      <Chip
                        label={table.exists ? 'Existe' : 'Faltante'}
                        color={table.exists ? 'success' : 'error'}
                        size="small"
                      />
                      {table.missingColumns?.length > 0 && (
                        <Chip
                          label={`${table.missingColumns.length} columnas faltantes`}
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Columnas Existentes:
                        </Typography>
                        <List dense>
                          {table.existingColumns?.map((col) => (
                            <ListItem key={col.name}>
                              <ListItemIcon>
                                <CheckIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={col.name}
                                secondary={`${col.type} ${col.nullable ? 'NULL' : 'NOT NULL'}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                      {table.missingColumns?.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom color="error">
                            Columnas Faltantes:
                          </Typography>
                          <List dense>
                            {table.missingColumns.map((col) => (
                              <ListItem key={col.name}>
                                <ListItemIcon>
                                  <ErrorIcon color="error" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={col.name}
                                  secondary={`${col.type} ${col.nullable ? 'NULL' : 'NOT NULL'}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </ModernCard>
    </Box>
  );

  const renderMigrationsTab = () => (
    <Box>
      <ModernCard title="Migraciones Disponibles" sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>⚠️ Precaución:</strong> Las migraciones modifican tu base de datos. 
            Se recomienda hacer un backup antes de ejecutar cualquier migración.
          </Alert>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : migrations.length === 0 ? (
            <Alert severity="success">
              No hay migraciones pendientes. Tu base de datos está actualizada.
            </Alert>
          ) : (
            <List>
              {migrations.map((migration) => (
                <Card key={migration.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Typography variant="h6" gutterBottom>
                          {migration.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {migration.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip
                            label={migration.version}
                            size="small"
                            color="primary"
                          />
                          <Chip
                            label={migration.type}
                            size="small"
                            color="info"
                          />
                          {migration.executed && (
                            <Chip
                              label="Ejecutada"
                              size="small"
                              color="success"
                              icon={<CheckIcon />}
                            />
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="Ver SQL">
                            <IconButton
                              onClick={() => {
                                setSqlCode(migration.sql);
                                setSelectedMigration(migration);
                                setShowSqlDialog(true);
                              }}
                            >
                              <CodeIcon />
                            </IconButton>
                          </Tooltip>
                          {!migration.executed && (
                            <GradientButton
                              startIcon={<ExecuteIcon />}
                              onClick={() => executeMigration(migration.id)}
                              disabled={executingSql}
                            >
                              Ejecutar
                            </GradientButton>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </List>
          )}
        </Box>
      </ModernCard>
    </Box>
  );

  const renderHistoryTab = () => (
    <Box>
      <ModernCard title="Historial de Migraciones">
        <Box sx={{ p: 2 }}>
          {migrationHistory.length === 0 ? (
            <Alert severity="info">
              No hay historial de migraciones aún.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Versión</TableCell>
                    <TableCell>Fecha de Ejecución</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Tiempo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {migrationHistory.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{record.name}</TableCell>
                      <TableCell>
                        <Chip label={record.version} size="small" />
                      </TableCell>
                      <TableCell>
                        {new Date(record.executed_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          color={record.status === 'success' ? 'success' : 'error'}
                          size="small"
                          icon={record.status === 'success' ? <CheckIcon /> : <ErrorIcon />}
                        />
                      </TableCell>
                      <TableCell>{record.execution_time || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </ModernCard>
    </Box>
  );

  return (
    <ModernLayout title="Gestor de Base de Datos">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Gestor de Base de Datos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitorea, compara y actualiza tu base de datos desde el dashboard
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Dashboard" icon={<StorageIcon />} iconPosition="start" />
          <Tab label="Comparar Esquema" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Migraciones" icon={<BuildIcon />} iconPosition="start" />
          <Tab label="Historial" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && renderDashboardTab()}
      {activeTab === 1 && renderSchemaTab()}
      {activeTab === 2 && renderMigrationsTab()}
      {activeTab === 3 && renderHistoryTab()}

      {/* SQL Dialog */}
      <Dialog
        open={showSqlDialog}
        onClose={() => setShowSqlDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMigration ? `SQL - ${selectedMigration.name}` : 'Ejecutar SQL Personalizado'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={15}
            value={sqlCode}
            onChange={(e) => setSqlCode(e.target.value)}
            placeholder="Escribe tu código SQL aquí..."
            sx={{
              mt: 2,
              fontFamily: 'monospace',
              '& textarea': {
                fontFamily: 'monospace',
                fontSize: '14px'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSqlDialog(false)}>
            Cancelar
          </Button>
          <Button
            startIcon={<CopyIcon />}
            onClick={() => copyToClipboard(sqlCode)}
          >
            Copiar
          </Button>
          {!selectedMigration && (
            <GradientButton
              startIcon={<ExecuteIcon />}
              onClick={executeCustomSql}
              disabled={executingSql || !sqlCode.trim()}
            >
              {executingSql ? 'Ejecutando...' : 'Ejecutar SQL'}
            </GradientButton>
          )}
        </DialogActions>
      </Dialog>

      <AlertSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </ModernLayout>
  );
};

export default DatabaseManager;

